import * as React from 'react';
import * as TreeRepository from '../../Repository/TreeRepository';
import * as ContractRepository from '../../Repository/ContractRepository';
import TreeMaps from './TreeMaps';
import TreeTable from "./TreeTable";
import ErrorPage from "../ErrorPage/ErrorPage";
import _ from 'lodash';
import voca from "voca";
import MapPopup from "./Popup/MapPopup";
import ValidatedTrees from "./ValidatedTrees";
import { Affix, Alert, Col, message, Row } from 'antd';
import { default as Page, PageProps } from '../../Component/Page/Page';
import { Tree, Pager, GeoJsonFile } from 'tree-wallet-client';
import { GlobalContext } from '../../Contexts/GlobalContext';
import { BrowserView, MobileView } from 'react-device-detect';
import { userHasRole, UserRoles } from '../../Repository/UserRepository';
import { isAllowed } from "../../Service/AuthService";
import { LngLatLike } from "mapbox-gl";
import Filters from './Filters/Filters';

export interface MapPosition {
  coordinates?: LngLatLike;
  treeId?: number;
  isInitial?: boolean;
  selected?: boolean;
  openPopup?: boolean;
  zoomLevel?: number;
}

interface ListPageState {
  pager?: MapPager;
  trees?: Tree[];
  loading: boolean;
  filter: Filter;
  geoJsonFiles?: GeoJsonFile[] | undefined;
  showBanner: boolean;
  bannerMessage?: string;
  bannerType?: 'success' | 'info' | 'warning' | 'error';
  mapLoading: boolean;
  /** Set while a validation is being written to the validator's queue (see ValidatedTrees). */
  queueSaving: boolean;
}

export interface MapPager extends Pager {
  totalTreesPlanted?: number;
}

export interface Filter {
  sortField: string;
  sortOrder: string;
  contractId?: number;
  treeId?: number;
  validatorId?: number;
  zoomOut: boolean;
  dateStart?: number;
  dateEnd?: number;
}

export const allowedRoles = [
  ...Object.values(ContractRepository.roles),
  UserRoles.CompensationProgram
];

export default class MapPage extends React.Component<PageProps, ListPageState> {
  static contextType = GlobalContext;
  declare context: React.ContextType<typeof GlobalContext>;
  declare mapRef: React.Ref<TreeMaps>;
  declare tableRef: React.Ref<TreeTable>;

  constructor(props: PageProps) {
    super(props);
    this.getTrees = this.getTrees.bind(this);
    this.updateState = this.updateState.bind(this);
    this.onDeleteTree = this.onDeleteTree.bind(this);
    this.mapRef = React.createRef();
    this.tableRef = React.createRef();
    this.state = {
      pager: {
        pageSize: 25,
        currentPage: 1,
      },
      trees: undefined,
      loading: true,
      filter: {
        zoomOut: true,
        sortField: 'createdAt',
        sortOrder: 'desc',
        contractId: undefined,
        treeId: undefined,
        validatorId: undefined,
        dateStart: undefined,
        dateEnd: undefined,
      },
      geoJsonFiles: undefined,
      showBanner: false,
      bannerMessage: undefined,
      bannerType: 'success',
      mapLoading: false,
      queueSaving: false,
    }
  }

  async componentDidMount() {
    const { contract } = this.context;

    const geoJsonFilesList = await TreeRepository.getGeoJsons({
      params: {
        ...(contract && { contractId: contract.id }),
      },
    });
    // getTrees method sets the state
    await this.getTrees(undefined, {
      ...this.state.filter,
    });
    this.setState({
      loading: false,
      geoJsonFiles: geoJsonFilesList.files
    });
  }

  async getTrees(params?: any, filter?: Filter, pager?: MapPager, errorCallback?: Function) {
    const { user } = this.context;
    const { filter: stateFilter, pager: statePager } = this.state;

    // Merge filters properly, with incoming filter taking precedence
    const compiledFilter = {
      ...stateFilter,
      ...(filter || {})
    };

    const compiledPager = _.merge(statePager, pager ? pager : {});

    console.log(compiledFilter);

    try {
      if (!user) {
        throw new Error('no user defined');
      }

      const response = await TreeRepository.getTreeList({
        queryParams: {
          filter: JSON.stringify({
            ...compiledFilter,
            ...(params && params),
          }),
          pageSize: compiledPager?.pageSize,
          page: compiledPager?.currentPage,
        },
        admin: userHasRole(user.roles, UserRoles.Admin.name),
        errorCallback: (error: any) => {
          errorCallback && errorCallback(error);
        }
      });

      // IMPORTANT: Update the state with the compiled filter to persist it
      this.setState({
        pager: {
          ...response.pager,
          totalTreesPlanted: response.extraData.treeCount
        },
        trees: response.data,
        filter: compiledFilter // Persist the filter state
      });

      return response;
    } catch (error) {
      console.error('There was an error fetching the tree list: ' + error.message);
      message.error('There was an error: ' + error.message);
      throw new Error('There was an error fetching the tree list');
    }
  }

  getDefaultPosition(): MapPosition {
    const { trees } = this.state;
    if (trees === undefined || trees.length === 0) {
      return {
        coordinates: [0, 0]
      };
    }
    const tree = trees[trees.length - 1];
    return {
      coordinates: [
        tree.lng,
        tree.lat
      ],
    }
  }

  onDeleteTree(treeId: number) {
    const { trees } = this.state;
    this.setState({
      trees: trees && trees.filter(tree => tree.id !== treeId)
    });
  }

  updateState(stateData: any) {
    this.setState({
      ...stateData
    });
  }

  getTreemap() {
    const { filter, geoJsonFiles } = this.state;
    const { contract } = this.context;

    if (!contract || !geoJsonFiles) {
      return null;
    }

    const { language } = contract.preferences;

    return <TreeMaps
      public={false}
      ref={this.mapRef}
      tableRef={this.tableRef}
      geoJsonFiles={geoJsonFiles}
      language={language}
      initialPosition={{ ...this.getDefaultPosition(), ...(filter.zoomOut ? { zoomLevel: 2 } : {}) }}
      template={MapPopup}
      onLoadingStateChange={(isLoading) => this.setState({ mapLoading: isLoading })}
    />
  }

  render() {
    const { trees, loading, filter, pager } = this.state;
    const { user, contract } = this.context;
    const pageProps = { ...this.props, loading }

    // Debug: Log the filter being passed to Page
    console.log('MapPage render - current filter:', filter);

    if (!contract) {
      console.error('no contract defined');
      return <Page {...this.props} hideMenu={false} title={'Tree List'} allowedRoles={allowedRoles}>
        <ErrorPage template={"generic"} subTitle={'No contract defined'} />
      </Page>
    }

    if (!user) {
      console.error('no user defined');
      return <Page {...this.props} hideMenu={false} title={'Tree List'} allowedRoles={allowedRoles}>
        <ErrorPage template={"generic"} subTitle={'No user defined'} />
      </Page>
    }

    const { language } = contract.preferences;

    return (
      <GlobalContext.Provider value={{ ...this.context, trees, filter, updateState: this.updateState }}>
        <Page {...pageProps} title={voca.capitalize(user.firstName)} allowedRoles={allowedRoles} filter={filter}>
          {this.state.showBanner && (
            <Alert
              message={this.state.bannerMessage || "Tree was added to queue"}
              type={this.state.bannerType || "success"}
              showIcon
              closable
              onClose={() => this.setState({ showBanner: false })}
              style={{ marginBottom: '16px' }}
            />
          )}
          {isAllowed(contract.roles, [ContractRepository.roles.Validator, ContractRepository.roles.Agent]) && (
            <Row>
              <Col xs={24} xl={8} md={0} style={{ paddingTop: '20px' }}>
                <div>
                  <ValidatedTrees getTrees={this.getTrees} saving={this.state.queueSaving} />
                </div>
              </Col>
            </Row>
          )}
          {(isAllowed(user.roles, [UserRoles.Admin, UserRoles.CompensationProgram]) || isAllowed(contract.roles, [ContractRepository.roles.Validator, ContractRepository.roles.Agent, ContractRepository.roles.Coordinator])) &&
            <Row>
              <Col span={24}>
                <Filters
                  trees={trees}
                  mapRef={this.mapRef}
                  filter={filter}
                  getTrees={this.getTrees}
                  updateState={this.updateState}
                  loading={this.state.mapLoading || loading}
                />
              </Col>
            </Row>
          }
          <Row>
            {/* No fixed breakpoint width here: while no tree is loaded the table
                renders nothing (or its "no data" placeholder), but a column sized
                by `md`/`xl` would still reserve a quarter of the row - and those
                breakpoints match a phone in landscape, leaving a blank strip on
                the left of the map. Sized by its content, the column collapses to
                zero width when the table is not rendered. */}
            <Col>
              <BrowserView>
                <TreeTable
                  mapRef={this.mapRef}
                  ref={this.tableRef}
                  language={language}
                  filter={filter}
                  trees={trees}
                  onDeleteTree={this.onDeleteTree}
                  getTrees={this.getTrees}
                  pagerData={pager}
                  updateState={this.updateState}
                  paginationConfig={{
                    defaultCurrent: pager?.currentPage,
                    pageSize: pager?.pageSize,
                    total: pager?.totalRecords,
                    pageSizeOptions: [25, 50, 100],
                    showLessItems: true,
                    showSizeChanger: true,
                  }}
                />
              </BrowserView>
            </Col>
            <Col xs={24} xl={18} flex="auto">
              <BrowserView>
                <Affix offsetTop={100}>
                  {this.getTreemap()}
                </Affix>
              </BrowserView>
              <MobileView>
                {this.getTreemap()}
              </MobileView>
            </Col>
          </Row>
        </Page>
      </GlobalContext.Provider>
    );
  }
}
